import { injectable, inject } from 'inversify';
import {
  IAuthService,
  AuthenticateOptions,
  IApiService,
  IDoctorService,
  IPatientService,
  ICenterService,
  RecoveryResponse,
} from '@/interfaces/Services';
import {
  auth,
  doctor as doctorStore,
  patient as patientStore,
  center as centerStore,
} from '@/store/modules';
import { UserModel } from '@/models/user';
import DependencyIdentifiers from '@/ioc/DependencyIdentifiers';
import { ExportLock } from '@/models/doctor';

@injectable()
export default class AuthService implements IAuthService {
  @inject(DependencyIdentifiers.ApiService)
  private api!: IApiService;
  @inject(DependencyIdentifiers.DoctorService)
  private doctorService!: IDoctorService;
  @inject(DependencyIdentifiers.PatientService)
  private patientService!: IPatientService;
  @inject(DependencyIdentifiers.CenterService)
  private centerService!: ICenterService;

  private refreshTimer?: NodeJS.Timeout;

  /**
   * Does an authentication request.
   * @todo use path: `/authenticates`
   */
  async authenticate({ email, password }: AuthenticateOptions): Promise<string> {
    return this.api
      .request<any>(
        '/authenticates',
        {
          headers: {
            Authorization: `Basic ${btoa(unescape(encodeURIComponent(`${email}:${password}`))).toString()}`,
          },
          method: 'post',
          skipHeaders: true,
        }
      )
      .then(res => res.data.token);
  }

  async changeEmail(newEmail: string): Promise<void> {
    const data = { newEmail };
    await this.api
      .patch<any>(`/profile/change_email`, { data })
      .then(res => res.data);
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    const data = {
      newPassword,
      oldPassword,
    };
    await this.api
      .patch<any>(`/profile/change_password`, { data })
      .then(res => res.data);
  }

  async passwordResetRequest(email: string): Promise<void> {
    const data = { email };
    return this.api
      .post<any>(`/authenticates/password_resets`, { data })
      .then(res => res.data);
  }

  async passwordReset(token: string, password: string, recoveryKey: string): Promise<void> {
    const data = { token, password, recoveryKey };
    return this.api
      .patch<any>(`/authenticates/password_resets`, { data })
      .then(res => res.data);
  }

  /**
   * Logs the user out of the application.
   * @todo implement server side
   */
  async logout(): Promise<void> {
    /// await this.post('/authenticates/logout');
    auth.setUnauthenticated();
    this.cancelScheduledTokenRefresh();
  }

  /**
   * Extends the current refresh token.
   */
  async extendRefreshToken(): Promise<string> {
    const { data: { token } } = await this.api
      .post<{ data: { token: string } }>('/authenticates/extends', { data: { token: auth.refreshToken }});
    return token;
  }

  async recovery(): Promise<RecoveryResponse> {
    const { data: { file, key } } = await this.api
      .get<{ data: RecoveryResponse }>('/profile/recovery');
    return { key, file: atob(file)};
  }

  async signUrl(urlUnsigned: string): Promise<string> {
    const { data: { url } } = await  this.api
      .post('/authenticates/sign_url', { data: { url: urlUnsigned } });
    return url;
  }

  async fork(): Promise<string> {
    const { data: { token } } = await  this.api
      .post('/authenticates/fork');
    return token;
  }

  async register(
    token: string,
    code: string,
    password: string,
  ): Promise<UserModel> {
    const data = { token, code, password, };
    return await this.api
      .post<any>('/authenticates/register', { data })
      .then(res => res.data);
  }

  /**
   * Load user session from storage and update client side cache data.
   */
  async loadSession(): Promise<void> {
    auth.resume();
    if (auth.isAuthenticated) {
      try {
        await this.cacheMetadata();
        await this.scheduleTokenRefresh();
      } catch (e) {
        console.error(e);
        // setTimeout(() => window.location.reload(), 5000);
      }
    }
  }

  /**
   * Checks if the token is still valid and if so then updates the old one
   * othervise it will throw out the previous token.
   */
  async scheduleTokenRefresh() {
    if (!auth.isAuthenticated) { return; }

    await this.extendRefreshToken()
      .then((token) => auth.setAuthenticated(token))
      .catch((err) => {
        auth.setUnauthenticated();

        // Not the best solution, but the app must be refreshed.
        // We could channel this event to a Vue instance, but auth store cannot handle
        // this either. Refatctor later.
        window.location.reload();
      })
      .finally(() => {
        if (!auth.expiry) { return; }

        const timer = (auth.expiry * 1000) - Date.now() - 5000;
        this.refreshTimer = setTimeout(async () => this.scheduleTokenRefresh(), timer);
      });
  }
  
  cancelScheduledTokenRefresh(): void {
    if (this.refreshTimer == null) { return; }
    clearTimeout(this.refreshTimer);
  }

  /**
   * Load auth related store caches (this should be called after sign-in.)
   */
  async cacheMetadata(): Promise<void> {
    if (!auth.isAuthenticated) { return; }

    this.cancelScheduledTokenRefresh();
    await this.scheduleTokenRefresh();

    // For foreign center related data caching. Must be reverted at the end of 
    // caching.
    auth.context.commit('setIgnoreCenterIdOverride', true);

    if (auth.authDoctorId !== null) {
      const doctor = await this.doctorService.getById(`${auth.authDoctorId}`);
      doctorStore.cacheOne(doctor);
      auth.context.commit('setExportLock', doctor.$exportLock || ExportLock.None);
    }

    if (auth.authPatientId !== null) {
      await this.patientService
        .getById(`${auth.authPatientId}`)
        .then(patient => patientStore.cacheOne(patient));
    }

    if (auth.isPrimaryCenter && auth.isManager) {
      await this.centerService.getMany(1, 100);
    } else {
      await this.centerService
        .getById(`${auth.authCenterId}`)
        .then(center => centerStore.cacheOne(center));
    }

    // Rollback to default behaviour.
    auth.context.commit('setIgnoreCenterIdOverride', false);
  }
}
